import React, { useState, useEffect, useRef } from 'react';
import { createLicense, getLicense, updateLicense, uploadLicenseFile, deleteLicenseAttachment, getUsersLookup } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Save, FileText, Upload, File, X, AlertTriangle, Mail, Check, Search, Download, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import DatePicker from '../components/DatePicker';
import DayChipsInput from '../components/DayChipsInput';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';

const EMPTY_FORM = {
  name: '', type: '', category: 'kn', description: '',
  issuing_authority: '', license_number: '', protocol_number: '', area: '', issue_date: '',
  expiry_date: '', indeterminate_expiry: false, is_critical: false,
  responsible_person: '', notes: '', notification_recipients: [],
  notification_days_before: [60, 30, 15, 7, 1],
  notification_days_after: [1, 7, 15]
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

const LicenseFormDialog = ({ open, onOpenChange, licenseId = null, onSuccess }) => {
  const isEditing = Boolean(licenseId);
  const fileInputRef = useRef(null);
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [deletingAttId, setDeletingAttId] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [allUsers, setAllUsers] = useState([]);
  const [recipientsOpen, setRecipientsOpen] = useState(false);
  const [recipientSearch, setRecipientSearch] = useState('');

  // Carrega lista de usuários para o multi-select de destinatários
  useEffect(() => {
    if (!open) return;
    let mounted = true;
    getUsersLookup()
      .then(r => { if (mounted) setAllUsers(r.data); })
      .catch(() => { /* silent */ });
    return () => { mounted = false; };
  }, [open]);

  useEffect(() => {
    let mounted = true;
    if (!open) {
      const t = setTimeout(() => {
        if (!mounted) return;
        setFormData({ ...EMPTY_FORM });
        setSelectedFiles([]);
        setAttachments([]);
        setRecipientsOpen(false);
      }, 0);
      return () => { mounted = false; clearTimeout(t); };
    }
    if (!licenseId) return () => { mounted = false; };
    const loadLicense = async () => {
      if (mounted) setLoading(true);
      try {
        const res = await getLicense(licenseId);
        if (!mounted) return;
        const l = res.data;
        setFormData({
          name: l.name || '', type: l.type || '', category: l.category || 'kn',
          description: l.description || '', issuing_authority: l.issuing_authority || '',
          license_number: l.license_number || '',
          protocol_number: l.protocol_number || '',
          area: l.area || '',
          issue_date: l.issue_date?.split('T')[0] || '',
          expiry_date: l.expiry_date?.split('T')[0] || '',
          indeterminate_expiry: l.indeterminate_expiry || false,
          is_critical: l.is_critical || false,
          responsible_person: l.responsible_person || '', notes: l.notes || '',
          notification_recipients: l.notification_recipients || [],
          notification_days_before: Array.isArray(l.notification_days_before) && l.notification_days_before.length > 0 ? l.notification_days_before : [60, 30, 15, 7, 1],
          notification_days_after: Array.isArray(l.notification_days_after) && l.notification_days_after.length > 0 ? l.notification_days_after : [1, 7, 15]
        });
        if (Array.isArray(l.attachments) && l.attachments.length > 0) {
          setAttachments(l.attachments);
        } else if (l.file_url) {
          setAttachments([{ id: 'legacy', file_name: l.file_name || 'Arquivo anexado', file_url: l.file_url, uploaded_at: l.updated_at }]);
        } else {
          setAttachments([]);
        }
      } catch (e) {
        if (mounted) {
          toast.error('Erro ao carregar licença');
          onOpenChange(false);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    loadLicense();
    return () => { mounted = false; };
  }, [open, licenseId, onOpenChange]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => setFormData(prev => ({ ...prev, [name]: value }));

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const valid = [];
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) { toast.error(`"${file.name}" é muito grande. Máximo 10MB.`); continue; }
      valid.push(file);
    }
    if (valid.length) setSelectedFiles(prev => [...prev, ...valid]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeSelectedFile = (idx) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleDeleteAttachment = async (att) => {
    if (!window.confirm(`Excluir o anexo "${att.file_name}"?`)) return;
    setDeletingAttId(att.id);
    try {
      await deleteLicenseAttachment(licenseId, att.id);
      setAttachments(prev => prev.filter(a => a.id !== att.id));
      toast.success('Anexo removido');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao remover anexo');
    } finally {
      setDeletingAttId(null);
    }
  };

  const formatCNPJ = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 14) {
      return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return value;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const dataToSend = {
        ...formData,
        area: formData.category === 'kn' ? (formData.area || '') : '',
        issue_date: formData.issue_date ? new Date(formData.issue_date).toISOString() : '',
        expiry_date: formData.indeterminate_expiry ? null : (formData.expiry_date ? new Date(formData.expiry_date).toISOString() : null)
      };

      let id = licenseId;
      if (isEditing) {
        await updateLicense(licenseId, dataToSend);
        toast.success('Licença atualizada com sucesso');
      } else {
        const response = await createLicense(dataToSend);
        id = response.data.id;
        toast.success('Licença criada com sucesso');
      }

      if (selectedFiles.length > 0 && id) {
        setUploading(true);
        let okCount = 0;
        for (const f of selectedFiles) {
          try { await uploadLicenseFile(id, f); okCount++; }
          catch { toast.error(`Erro ao anexar "${f.name}"`); }
        }
        if (okCount > 0) toast.success(`${okCount} arquivo(s) anexado(s)`);
        setUploading(false);
      }

      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar licença');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-gray-200 sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="license-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-gray-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#003366]" />
            {isEditing ? 'Editar Licença' : 'Nova Licença'}
          </DialogTitle>
          <DialogDescription className="text-gray-500">
            {isEditing ? 'Atualize as informações da licença' : 'Preencha os dados da nova licença'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="space-y-4 py-4">
            {[...Array(5)].map((_, i) => (<Skeleton key={i} className="h-10 bg-gray-100" />))}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5 py-2">
            {/* Name and Type */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700 font-semibold">Nome da Licença *</Label>
                <Input name="name" value={formData.name} onChange={handleChange} placeholder="Ex: Licença Ambiental - Unidade SP" className="bg-white border-gray-300 text-gray-900" data-testid="dialog-name-input" required />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700 font-semibold">Tipo *</Label>
                <Select value={formData.type} onValueChange={(v) => handleSelectChange('type', v)} required>
                  <SelectTrigger className="bg-white border-gray-300 text-gray-900" data-testid="dialog-type-select">
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 max-h-[300px]">
                    <SelectItem value="ae">AE</SelectItem>
                    <SelectItem value="afe">AFE</SelectItem>
                    <SelectItem value="alvara">Alvará</SelectItem>
                    <SelectItem value="antt">ANTT</SelectItem>
                    <SelectItem value="avcb">AVCB</SelectItem>
                    <SelectItem value="certidao_crf">Certidão de Regularidade CRF</SelectItem>
                    <SelectItem value="certidao_federal">Certidão Negativa de Débitos Federal</SelectItem>
                    <SelectItem value="cetesb">CETESB</SelectItem>
                    <SelectItem value="cnpj">CNPJ</SelectItem>
                    <SelectItem value="marinha_mercante">Marinha Mercante</SelectItem>
                    <SelectItem value="crq">CRQ</SelectItem>
                    <SelectItem value="licenca_ambiental">Licença Ambiental</SelectItem>
                    <SelectItem value="policia_civil">Polícia Civil</SelectItem>
                    <SelectItem value="policia_federal">Polícia Federal</SelectItem>
                    <SelectItem value="exercito">Exército</SelectItem>
                    <SelectItem value="licenca_sanitaria">Licença Sanitária</SelectItem>
                    <SelectItem value="vre">VRE</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label className="text-gray-700 font-semibold">Categoria *</Label>
              <Select value={formData.category} onValueChange={(v) => handleSelectChange('category', v)} required>
                <SelectTrigger className="bg-white border-gray-300 text-gray-900" data-testid="dialog-category-select">
                  <SelectValue placeholder="Selecione a categoria" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  <SelectItem value="kn">Kuehne+Nagel</SelectItem>
                  <SelectItem value="fornecedor">Fornecedor</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Area (KN only) */}
            {formData.category === 'kn' && (
              <div className="space-y-2">
                <Label className="text-gray-700 font-semibold">Área (Kuehne+Nagel)</Label>
                <Select value={formData.area || 'none'} onValueChange={(v) => handleSelectChange('area', v === 'none' ? '' : v)}>
                  <SelectTrigger className="bg-white border-gray-300 text-gray-900" data-testid="dialog-area-select">
                    <SelectValue placeholder="Selecione a área" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="none">Nenhuma</SelectItem>
                    <SelectItem value="fiscal">Fiscal</SelectItem>
                    <SelectItem value="juridico">Jurídico</SelectItem>
                    <SelectItem value="licenciamento">Licenciamento/Regulatório</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">Agrupa esta licença em um dos cards de área do Dashboard.</p>
              </div>
            )}

            {/* CNPJ and Issuing Authority */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700 font-semibold">CNPJ *</Label>
                <Input name="license_number" value={formData.license_number} onChange={(e) => setFormData(prev => ({ ...prev, license_number: formatCNPJ(e.target.value) }))} placeholder="00.000.000/0000-00" maxLength={18} className="bg-white border-gray-300 text-gray-900 font-mono" data-testid="dialog-cnpj-input" required />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700 font-semibold">Órgão Emissor *</Label>
                <Input name="issuing_authority" value={formData.issuing_authority} onChange={handleChange} placeholder="Ex: CETESB, IBAMA" className="bg-white border-gray-300 text-gray-900" data-testid="dialog-issuing-authority-input" required />
              </div>
            </div>

            {/* Protocol Number */}
            <div className="space-y-2">
              <Label className="text-gray-700 font-semibold">Número de Protocolo</Label>
              <Input name="protocol_number" value={formData.protocol_number} onChange={handleChange} placeholder="Ex: PROT-2025-001 (opcional)" className="bg-white border-gray-300 text-gray-900" data-testid="dialog-protocol-input" />
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700 font-semibold">Data de Emissão *</Label>
                <DatePicker
                  value={formData.issue_date}
                  onChange={(v) => setFormData(prev => ({ ...prev, issue_date: v }))}
                  data-testid="dialog-issue-date-input"
                  placeholder="Selecionar data de emissão"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700 font-semibold">Data de Vencimento *</Label>
                <DatePicker
                  value={formData.expiry_date}
                  onChange={(v) => setFormData(prev => ({ ...prev, expiry_date: v }))}
                  data-testid="dialog-expiry-date-input"
                  placeholder="Selecionar data de vencimento"
                  disabled={formData.indeterminate_expiry}
                />
              </div>
            </div>

            {/* Checkboxes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <Checkbox id="dialog-indeterminate" checked={formData.indeterminate_expiry} onCheckedChange={(c) => setFormData({ ...formData, indeterminate_expiry: c, expiry_date: c ? '' : formData.expiry_date })} className="border-gray-400 data-[state=checked]:bg-[#003366]" data-testid="dialog-indeterminate-checkbox" />
                <Label htmlFor="dialog-indeterminate" className="text-gray-700 cursor-pointer text-sm font-medium">Vencimento Indeterminado</Label>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-red-50 rounded-lg border border-red-200">
                <Checkbox id="dialog-critical" checked={formData.is_critical} onCheckedChange={(c) => setFormData({ ...formData, is_critical: c })} className="border-red-400 data-[state=checked]:bg-red-600" data-testid="dialog-critical-checkbox" />
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                  <Label htmlFor="dialog-critical" className="text-red-700 cursor-pointer text-sm font-medium">Item Crítico</Label>
                </div>
              </div>
            </div>

            {/* Responsible Person */}
            <div className="space-y-2">
              <Label className="text-gray-700 font-semibold">Responsável</Label>
              <Input name="responsible_person" value={formData.responsible_person} onChange={handleChange} placeholder="Nome do responsável" className="bg-white border-gray-300 text-gray-900" data-testid="dialog-responsible-input" />
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <Label className="text-gray-700 font-semibold">Arquivos Anexos</Label>
              {attachments.length > 0 && (
                <div className="space-y-1.5" data-testid="license-attachments-list">
                  {attachments.map((att) => (
                    <div key={att.id} className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-lg border border-gray-200" data-testid={`attachment-item-${att.id}`}>
                      <File className="w-4 h-4 text-[#003366] flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-700 text-sm truncate font-medium">{att.file_name}</p>
                        <p className="text-xs text-gray-500">
                          {formatDateTime(att.uploaded_at)}
                          {att.uploaded_by_name ? ` · ${att.uploaded_by_name}` : ''}
                          {att.size_bytes ? ` · ${(att.size_bytes / 1024 / 1024).toFixed(2)} MB` : ''}
                        </p>
                      </div>
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}${att.file_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        download={att.file_name}
                        className="flex items-center gap-1 text-[#003366] text-sm font-semibold hover:underline flex-shrink-0"
                        data-testid={`attachment-download-${att.id}`}
                      >
                        <Download className="w-3.5 h-3.5" /> Baixar
                      </a>
                      {hasPermission('licenses', 'edit') && att.id !== 'legacy' && (
                        <Button
                          type="button" variant="ghost" size="icon"
                          className="h-7 w-7 text-gray-400 hover:text-rose-500 flex-shrink-0"
                          onClick={() => handleDeleteAttachment(att)}
                          disabled={deletingAttId === att.id}
                          data-testid={`attachment-delete-${att.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {selectedFiles.map((file, idx) => (
                <div key={`${file.name}-${idx}`} className="flex items-center gap-3 p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <File className="w-4 h-4 text-emerald-600" />
                  <span className="text-gray-700 flex-1 text-sm truncate">{file.name}</span>
                  <span className="text-xs text-emerald-700 font-medium">novo</span>
                  <span className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                  <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-gray-400 hover:text-rose-500" onClick={() => removeSelectedFile(idx)}><X className="w-3.5 h-3.5" /></Button>
                </div>
              ))}
              <div className="flex items-center gap-3">
                <input ref={fileInputRef} type="file" multiple onChange={handleFileSelect} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" className="hidden" data-testid="dialog-file-input" />
                <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} className="bg-white text-gray-700 border-gray-300 text-sm" data-testid="dialog-add-file-btn">
                  <Upload className="w-4 h-4 mr-2" />Adicionar arquivo(s)
                </Button>
                <span className="text-xs text-gray-500">PDF, DOC, JPG, PNG (máx. 10MB cada)</span>
              </div>
            </div>

            {/* Notification Recipients */}
            <div className="space-y-2">
              <Label className="text-gray-700 font-semibold flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#003366]" />
                Destinatários de Notificação de Vencimento
              </Label>
              <p className="text-xs text-gray-500">
                Selecione os usuários que receberão alertas por email quando esta licença estiver próxima do vencimento.
              </p>
              <div className="border border-gray-300 rounded-md bg-white">
                <button
                  type="button"
                  onClick={() => setRecipientsOpen(o => { if (o) setRecipientSearch(''); return !o; })}
                  className="w-full flex items-center justify-between px-3 py-2.5 text-sm hover:bg-gray-50 transition-colors"
                  data-testid="dialog-recipients-toggle"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    {formData.notification_recipients.length === 0 ? (
                      <span className="text-gray-400">Nenhum destinatário selecionado</span>
                    ) : (
                      formData.notification_recipients.slice(0, 4).map(uid => {
                        const u = allUsers.find(x => x.id === uid);
                        if (!u) return null;
                        return (
                          <Badge key={uid} variant="outline" className="bg-blue-50 text-[#003366] border-blue-200">
                            {u.name}
                          </Badge>
                        );
                      })
                    )}
                    {formData.notification_recipients.length > 4 && (
                      <Badge variant="outline" className="bg-gray-100 text-gray-700 border-gray-200">
                        +{formData.notification_recipients.length - 4}
                      </Badge>
                    )}
                  </div>
                  <X className={`w-4 h-4 text-gray-400 transition-transform ${recipientsOpen ? 'rotate-45' : 'rotate-0'}`} />
                </button>
                {recipientsOpen && (
                  <div className="border-t border-gray-200" data-testid="dialog-recipients-list">
                    <div className="sticky top-0 bg-white border-b border-gray-100 px-2 py-2 z-10">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        <Input
                          type="text"
                          value={recipientSearch}
                          onChange={(e) => setRecipientSearch(e.target.value)}
                          placeholder="Buscar por nome, email ou perfil..."
                          className="pl-8 bg-white border-gray-200 text-gray-900 h-9 text-sm"
                          autoFocus
                          data-testid="dialog-recipients-search"
                        />
                      </div>
                    </div>
                    <div className="max-h-56 overflow-y-auto">
                      {allUsers.length === 0 ? (
                        <div className="px-3 py-4 text-sm text-gray-400 text-center">Carregando usuários...</div>
                      ) : (() => {
                        const q = recipientSearch.trim().toLowerCase();
                        const filtered = q
                          ? allUsers.filter(u =>
                              (u.name || '').toLowerCase().includes(q) ||
                              (u.email || '').toLowerCase().includes(q) ||
                              (u.role || '').toLowerCase().includes(q)
                            )
                          : allUsers;
                        if (filtered.length === 0) {
                          return (
                            <div className="px-3 py-4 text-sm text-gray-400 text-center">
                              Nenhum usuário encontrado para &quot;{recipientSearch}&quot;
                            </div>
                          );
                        }
                        return filtered.map(u => {
                          const selected = formData.notification_recipients.includes(u.id);
                          return (
                            <button
                              key={u.id}
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  notification_recipients: selected
                                    ? prev.notification_recipients.filter(id => id !== u.id)
                                    : [...prev.notification_recipients, u.id]
                                }));
                              }}
                              className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-blue-50 transition-colors ${selected ? 'bg-blue-50' : ''}`}
                              data-testid={`dialog-recipient-${u.id}`}
                            >
                              <div className="flex flex-col items-start text-left">
                                <span className="font-medium text-gray-800">{u.name}</span>
                                <span className="text-xs text-gray-500">{u.email} · {u.role}</span>
                              </div>
                              {selected && <Check className="w-4 h-4 text-[#003366]" />}
                            </button>
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Notification Windows (per license) */}
            <div className="space-y-3 p-3 bg-blue-50/40 rounded-lg border border-blue-100">
              <div>
                <Label className="text-gray-700 font-semibold flex items-center gap-2">
                  <Mail className="w-4 h-4 text-[#003366]" />
                  Janelas de Notificação por Email
                </Label>
                <p className="text-xs text-gray-500 mt-1">
                  Defina em quantos dias <strong>antes</strong> e <strong>após</strong> o vencimento esta licença disparará alertas para os destinatários selecionados.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs font-semibold text-blue-900 uppercase tracking-wide">
                    Antes do vencimento
                  </Label>
                  <DayChipsInput
                    value={formData.notification_days_before}
                    onChange={(arr) => setFormData(prev => ({ ...prev, notification_days_before: arr }))}
                    accent="blue"
                    testIdPrefix="days-before"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold text-rose-900 uppercase tracking-wide">
                    Após o vencimento
                  </Label>
                  <DayChipsInput
                    value={formData.notification_days_after}
                    onChange={(arr) => setFormData(prev => ({ ...prev, notification_days_after: arr }))}
                    accent="rose"
                    testIdPrefix="days-after"
                  />
                </div>
              </div>
            </div>

            {/* Description and Notes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700 font-semibold">Descrição</Label>
                <Textarea name="description" value={formData.description} onChange={handleChange} placeholder="Breve descrição..." rows={2} className="bg-white border-gray-300 text-gray-900 resize-none" data-testid="dialog-description-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700 font-semibold">Observações</Label>
                <Textarea name="notes" value={formData.notes} onChange={handleChange} placeholder="Observações adicionais..." rows={2} className="bg-white border-gray-300 text-gray-900 resize-none" data-testid="dialog-notes-input" />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="bg-white text-gray-700 border-gray-300">Cancelar</Button>
              <Button type="submit" disabled={saving || uploading} className="bg-[#003366] hover:bg-[#002244] text-white font-semibold" data-testid="dialog-save-btn">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Salvando...' : uploading ? 'Enviando arquivo...' : (isEditing ? 'Salvar Alterações' : 'Criar Licença')}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LicenseFormDialog;
